import { useLockBankAccount } from '@/hooks/requests/useBankAccounts';
import {
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { SVButton, SVForm, SVSelect } from '../../core';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import { useForm } from 'react-hook-form';
import { SelectOption } from '@/types/inputs';
import {
  BankAccountLockReasons,
  LockReasonsFormValues,
} from '@/types/bankAccounts';

type BankAccountLockModalProps = {
  bankAccountId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function BankAccountLockModal({
  bankAccountId,
  open,
  setOpen,
}: BankAccountLockModalProps) {
  const { lockBankAccount, isLockBankAccountPending } = useLockBankAccount();

  const formMethods = useForm<LockReasonsFormValues>({
    defaultValues: {
      uuid: bankAccountId,
      lockReason: '',
    },
    mode: 'onBlur',
  });

  const lockAccountListItems = [
    'Cancel all pending Wire and ACH transactions.',
    'Prohibit owners from initiating new transactions.',
    'Prohibit staff and advisors from sending transaction requests.',
    "Send all owners an email confirming the account's locked status.",
  ];
  const lockReasons: SelectOption[] = [
    {
      label: 'Potential identity fraud',
      value: BankAccountLockReasons.BEING_INVESTIGATED,
    },
    {
      label: 'Potentially fraudulent ACH transactions',
      value: BankAccountLockReasons.INVESTIGATED_ACH,
    },
    {
      label: 'Potentially fraudulent Wire transactions',
      value: BankAccountLockReasons.INVESTIGATED_WIRE,
    },
    {
      label: 'Potentially fraudulent check issuance',
      value: BankAccountLockReasons.CHECK_ISSUED,
    },
    {
      label: 'Potentially fraudulent check deposit',
      value: BankAccountLockReasons.CHECK_DEPOSIT,
    },
    {
      label: 'Potentially fraudulent card transactions',
      value: BankAccountLockReasons.INVESTIGATED_CARD,
    },
  ];

  async function handleLockBankAccount(data: LockReasonsFormValues) {
    try {
      await lockBankAccount(data);
      showToast({
        type: 'success',
        data: { content: 'Bank account has been successfully locked' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <SVForm methods={formMethods} onSubmit={handleLockBankAccount}>
        <DialogContent>
          <Typography variant="h4">Lock Bank Account?</Typography>
          <Typography>
            Are you sure you want to lock this account? Locking the account
            will:
          </Typography>
          <List>
            {lockAccountListItems.map(rule => (
              <ListItem
                sx={{
                  p: '2px 0',
                  ml: '30px',
                  display: 'list-item',
                  listStyleType: 'disc',
                }}
                key={rule}>
                <Typography>{rule}</Typography>
              </ListItem>
            ))}
          </List>
          <Typography variant="h6">Reason for Locking?</Typography>
          <SVSelect
            name="lockReason"
            label="Reason for Locking"
            options={lockReasons}
            rules={{ required: true }}
          />
        </DialogContent>
        <DialogActions>
          <SVButton variant="outlined" onClick={() => setOpen(false)}>
            No, Don't Lock
          </SVButton>
          <SVButton
            type="submit"
            isLoading={isLockBankAccountPending}
            onClick={formMethods.handleSubmit(handleLockBankAccount)}>
            Yes, Lock
          </SVButton>
        </DialogActions>
      </SVForm>
    </Dialog>
  );
}
